import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import {
    loadFinancialPaymentMethodDetailsByPaymentMethodType,
    loadFinancialPaymentMethodTypeDetails,
    loadFinancialRequestReasonDetailsByRequestType,
    loadFinancialRequestStatusDetails,
    loadFinancialRequestTypeDetails
} from '../../../../store/directusService';
import {
    createFinancialRequest,
    loadContractConstants
} from '../../../../store/participantService';
import { searchUsersByLoggedInUserServiceIds } from '../../../../store/userService';
import { getConfiguredItems, getObjFromId } from '../../../../utils/directusFunctions';
import { TODAY } from '../../../../utils/formValidation/commonConstants';
import {
    CONTRACT_CONSTANTS_ERROR,
    FINANCIAL_REQUESTS_PAYMENT_METHOD_TYPE_ERROR,
    FINANCIAL_REQUESTS_STATUS_ERROR,
    FINANCIAL_REQUESTS_TYPE_ERROR
} from '../../../../utils/formValidation/loadingErrorMessageConstants';
import { formatFloat } from '../../../../utils/numberUtils';
import { addEmailAddressAsNameToArray } from '../../../../utils/userArrayUtils';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../../utils/userRoles';
import RadioButtons from '../../../formElements/RadioButtons';
import SingleSelect from '../../../formElements/SingleSelect';
import StaticField from '../../../formElements/StaticField';
import TextAreaField from '../../../formElements/TextAreaField';
import TextInputField from '../../../formElements/TextInputField';
import ESignatureModal from '../../../signatureDialog/ESignatureModal';
import FormActions from '../../../ui/formActions/FormActions';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import { MAX_REASON_DETAILS_LENGTH, validate } from './shared/validateFinancialRequest';

import app from '../../../../app.module.css';
import form from '../../../../commonStyles/formStyles.module.css';

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const adminRoles = [QUALITY, SUPERUSER];
const adviserRoles = [ADVISER, MANAGER, RECRUITMENT_MANAGER, SUPERUSER];

const CreateFinancialRequest = ({ roles, onClose }) => {
    // HOOKS
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { state } = useLocation();

    // LOCAL STATE
    const initialState = {
        id: null,
        code: '',
        adviserId: '',
        amount: '',
        approverId: '',
        requestTypeId: '',
        paymentMethodId: '',
        paymentMethodTypeId: '',
        paymentReasonId: '',
        dateIssued: '',
        dateRequested: '',
        statusChangeDate: '',
        taxNiIssued: false,
        reasonDetail: '',
        approverNote: '',
        statusId: '',
        participantId: '',
        authoriserSignature: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [isDisabled, setIsDisabled] = useState(false);
    const [isAdminDisabled, setIsAdminDisabled] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [advisers, setAdvisers] = useState([]);
    const [arrayFinancialRequestType, setArrayFinancialRequestType] = useState([]);
    const [arrayPaymentMethodType, setArrayPaymentMethodType] = useState([]);
    const [stateChecked, setStateChecked] = useState(false);
    const [authorisersSignatureDialogOpen, setAuthorisersSignatureDialogOpen] = useState(false);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const users = useSelector((state) => state.entities.userService.usersByLoggedInUserServiceIds);
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const statusDetails = useSelector(
        (state) => state.entities.directusService.financialRequestStatusDetails
    );
    const requestTypeDetails = useSelector(
        (state) => state.entities.directusService.financialRequestTypeDetails
    );
    const requestReasonDetails = useSelector(
        (state) => state.entities.directusService.financialRequestReasonDetails
    );
    const paymentMethodTypeDetails = useSelector(
        (state) => state.entities.directusService.financialPaymentMethodTypeDetails
    );
    const paymentMethodDetails = useSelector(
        (state) => state.entities.directusService.financialPaymentMethodDetails
    );

    const usersMetaData = useSelector(
        (state) => state.entities.userService.usersByLoggedInUserServiceIdsMetaData
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USEEFFECTS
    useEffect(() => {
        if (!statusDetails?.length) dispatch(loadFinancialRequestStatusDetails());
        if (!requestTypeDetails?.length) dispatch(loadFinancialRequestTypeDetails());
        if (!paymentMethodTypeDetails?.length) dispatch(loadFinancialPaymentMethodTypeDetails());
    }, []);

    useEffect(() => {
        if (!state?.provision) return;
        setNewEntry((prev) => ({
            ...prev,
            amount: state?.provision.costPerParticipant,
            reasonDetail: state?.provision.title
        }));
    }, [state?.provision]);

    useEffect(() => {
        setIsDisabled(!hasRole(acceptedRoles, roles ?? state?.roles));
        setIsAdminDisabled(!hasRole(adminRoles, roles ?? state?.roles));
        setStateChecked(true);
    }, [roles, state?.Roles]);

    useEffect(() => {
        if (!currentParticipant?.id || !loggedInUser?.id) return;
        dispatch(loadContractConstants(currentParticipant.contractId));
        setNewEntry((prev) => ({
            ...prev,
            participantId: currentParticipant.id,
            adviserId: loggedInUser.id
        }));
        setAdvisersArray([loggedInUser]);
    }, [currentParticipant?.id, loggedInUser?.id]);

    useEffect(() => {
        if (!users.length) return;
        let updatedAdvisers = users.filter((el) =>
            el.userTypes?.find((entry) => adviserRoles.includes(entry.role))
        );
        if (!updatedAdvisers.length && !usersMetaData?.last) {
            onLoadMoreAdvisers();
            return;
        }
        if (newEntry.adviserId && !updatedAdvisers.some((el) => el.id === newEntry.adviserId)) {
            // Put selected adviser at the top of dropdown if it's not in the updated advisers array
            const selectedAdviser = advisers.find((el) => el.id === newEntry.adviserId);
            if (selectedAdviser) updatedAdvisers = [selectedAdviser, ...updatedAdvisers];
        }
        setAdvisersArray(updatedAdvisers);
    }, [users]);

    useEffect(() => {
        if (statusDetails?.length) {
            setNewEntry((prev) => ({ ...prev, statusId: getStatusId('requested') }));
        }
    }, [statusDetails]);

    useEffect(() => {
        if (requestTypeDetails?.length && arrayFinancialRequestType?.length === 0) {
            setArrayFinancialRequestType(
                getConfiguredItems(requestTypeDetails, currentParticipant?.contractId)
            );
        }
    }, [requestTypeDetails]);

    useEffect(() => {
        if (paymentMethodTypeDetails?.length && arrayPaymentMethodType?.length === 0) {
            setArrayPaymentMethodType(
                getConfiguredItems(paymentMethodTypeDetails, currentParticipant?.contractId)
            );
        }
    }, [paymentMethodTypeDetails]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (!newEntry.authoriserSignature) return;
        onAuthoriserSignatureComplete();
    }, [newEntry.authoriserSignature]);

    useEffect(() => {
        if (successMessage === 'Participant financial request has been added') {
            setIsButtonDisabled(false);
            onFormExit();
        }
    }, [successMessage]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false } }));
    };

    const loadingError = () => {
        if (!statusDetails?.length) return FINANCIAL_REQUESTS_STATUS_ERROR;
        if (!requestTypeDetails?.length) return FINANCIAL_REQUESTS_TYPE_ERROR;
        if (!paymentMethodTypeDetails?.length) return FINANCIAL_REQUESTS_PAYMENT_METHOD_TYPE_ERROR;
        if (!Object.keys(contractConstants)) return CONTRACT_CONSTANTS_ERROR;
    };

    const getStatusId = (name) =>
        statusDetails.find((item) => item.name.toLowerCase() === name)?.id || '';

    const setAdvisersArray = (advisers) => {
        const updatedAdvisers = addEmailAddressAsNameToArray(advisers);
        setAdvisers(updatedAdvisers);
    };

    // EVENT HANDLERS
    const onAmountChange = (e) => {
        clearError('amount');
        const inputAmount = formatFloat(e.target.value);
        const adviserAmount = +contractConstants.adviserApprovalLimit;
        const additionalChanges =
            inputAmount <= adviserAmount
                ? {
                      statusId: getStatusId('approved'),
                      approverId: loggedInUser.id,
                      approverNote: 'Automatically approved as under approval limit'
                  }
                : {
                      statusId: getStatusId('requested'),
                      approverId: null,
                      approverNote: null
                  };
        setNewEntry((prev) => ({ ...prev, amount: inputAmount, ...additionalChanges }));
    };
    const onRequestTypeChange = (chosenId) => {
        clearError('requestTypeId');
        clearError('paymentReasonId');
        setNewEntry((prev) => ({ ...prev, requestTypeId: chosenId, paymentReasonId: '' }));

        if (chosenId) {
            dispatch(loadFinancialRequestReasonDetailsByRequestType(chosenId));
        }
    };

    const onPaymentMethodTypeChange = (chosenId) => {
        clearError('paymentMethodTypeId');
        clearError('paymentMethodId');
        setNewEntry((prev) => ({ ...prev, paymentMethodTypeId: chosenId, paymentMethodId: '' }));

        if (chosenId) {
            dispatch(loadFinancialPaymentMethodDetailsByPaymentMethodType(chosenId));
        }
    };

    const onUpdate = (key, value) => {
        errors[key] && clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    // EVENT HANDLERS
    const onFormExit = () => {
        if (state?.accordionPanelFrom === 'courses') {
            navigate('/course_work_and_education', { state: { accordionPanel: 'courses' } });
        } else if (state?.accordionPanelFrom === 'appointments') {
            navigate('/appointments', { state: state.returnState });
        } else onClose();
    };

    const onLoadMoreAdvisers = () => {
        if (usersMetaData?.last) return;
        dispatch(
            searchUsersByLoggedInUserServiceIds(
                loggedInUser.serviceIds,
                !users.length ? 0 : usersMetaData?.number + 1
            )
        );
    };

    const onAuthoriserSignatureComplete = () => {
        setIsButtonDisabled(true);
        dispatch(createFinancialRequest(newEntry));
    };

    // FORM SUBMIT
    const onSubmit = (e) => {
        e.preventDefault();

        const newErrors = validate(newEntry, requestReasonDetails, paymentMethodDetails);

        if (Object.keys(newErrors)?.length > 0) {
            setErrors(newErrors);
            return;
        }

        if (+newEntry.amount <= +contractConstants.adviserApprovalLimit) {
            setAuthorisersSignatureDialogOpen(true);
        } else {
            setIsButtonDisabled(true);
            dispatch(createFinancialRequest(newEntry));
        }
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} time={3} />;

    let content = '';
    if (!stateChecked) content = 'State not checked';
    if (!stateChecked) return <LoadingSpinner content={content} />;

    // RENDER
    return (
        <div className={form.formWrapper}>
            <h2 className={app.sectionHeading}>Create Financial Request</h2>
            <form onSubmit={onSubmit} className={form.form} data-testid="create_fr_form">
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id={'adviserId'}
                            label={'Adviser'}
                            placeholder="Select adviser..."
                            disabled={isAdminDisabled}
                            mandatory={true}
                            capitalizeList={false}
                            menuItems={advisers || []}
                            selectedId={newEntry.adviserId || ''}
                            selected={advisers?.find((el) => el.id === newEntry.adviserId) || {}}
                            error={errors.adviserId}
                            onChange={(chosenId) => onUpdate('adviserId', chosenId)}
                            onLoadMoreItems={onLoadMoreAdvisers}
                            moreItemsToLoad={
                                !usersMetaData?.totalElements ||
                                users.length < usersMetaData?.totalElements
                            }
                        />
                        <TextInputField
                            id={'amount'}
                            label={'Amount'}
                            placeholder={'Enter amount'}
                            disabled={isDisabled}
                            mandatory={true}
                            maxLength={7}
                            value={newEntry.amount || ''}
                            error={errors.amount}
                            onChange={onAmountChange}
                        />
                        <SingleSelect
                            id={'requestTypeId'}
                            label={'Request Type'}
                            placeholder="Select request type..."
                            disabled={isDisabled}
                            mandatory={true}
                            menuItems={arrayFinancialRequestType || []}
                            selectedId={newEntry.requestTypeId || ''}
                            selected={
                                getObjFromId(arrayFinancialRequestType, newEntry.requestTypeId) ||
                                {}
                            }
                            error={errors.requestTypeId}
                            onChange={(chosenId) => onRequestTypeChange(chosenId)}
                        />
                        <SingleSelect
                            id={'paymentReasonId'}
                            label={'Request Reason'}
                            placeholder="Select request reason..."
                            disabled={
                                !newEntry.requestTypeId ||
                                !requestReasonDetails.length ||
                                isDisabled
                            }
                            mandatory={newEntry.requestTypeId && requestReasonDetails.length > 0}
                            menuItems={requestReasonDetails || []}
                            selectedId={newEntry.paymentReasonId || ''}
                            selected={
                                getObjFromId(requestReasonDetails, newEntry.paymentReasonId) || {}
                            }
                            error={errors.paymentReasonId}
                            onChange={(chosenId) => onUpdate('paymentReasonId', chosenId)}
                        />
                        <SingleSelect
                            id={'paymentMethodTypeId'}
                            label={'Payment Method Type'}
                            placeholder="Select payment method type..."
                            disabled={isDisabled}
                            mandatory={true}
                            menuItems={arrayPaymentMethodType || []}
                            selectedId={newEntry.paymentMethodTypeId || ''}
                            selected={
                                getObjFromId(
                                    arrayPaymentMethodType,
                                    newEntry.paymentMethodTypeId
                                ) || {}
                            }
                            error={errors.paymentMethodTypeId}
                            onChange={(chosenId) => onPaymentMethodTypeChange(chosenId)}
                        />
                        <SingleSelect
                            id={'paymentMethodId'}
                            label={'Payment Method'}
                            placeholder="Select payment method..."
                            disabled={
                                !newEntry.paymentMethodTypeId ||
                                !paymentMethodDetails.length ||
                                isDisabled
                            }
                            mandatory={
                                newEntry.paymentMethodTypeId && paymentMethodDetails.length > 0
                            }
                            menuItems={paymentMethodDetails || []}
                            selectedId={newEntry.paymentMethodId || ''}
                            selected={
                                getObjFromId(paymentMethodDetails, newEntry.paymentMethodId) || {}
                            }
                            error={errors.paymentMethodId}
                            onChange={(chosenId) => onUpdate('paymentMethodId', chosenId)}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <TextAreaField
                            id={'reasonDetail'}
                            label={'Reason Details'}
                            placeholder={'Enter reason detail'}
                            disabled={isDisabled}
                            mandatory={true}
                            rows={6}
                            value={newEntry.reasonDetail || ''}
                            count={`${newEntry.reasonDetail.length}/${MAX_REASON_DETAILS_LENGTH}`}
                            maxLength={MAX_REASON_DETAILS_LENGTH}
                            error={errors.reasonDetail}
                            onChange={(e) => onUpdate('reasonDetail', e.target.value)}
                        />
                        <StaticField
                            id="dateRequested"
                            label="Date Requested"
                            customClass="dateInfo"
                            content={TODAY}
                        />
                        <RadioButtons
                            id="taxNiIssued"
                            label="Tax and NI issued?"
                            disabled={isDisabled}
                            value={newEntry.taxNiIssued}
                            onChange={(option) => onUpdate('taxNiIssued', option)}
                        />
                    </div>
                </div>
                <FormActions
                    id="create"
                    btnText="Add Financial Request"
                    disabled={isButtonDisabled}
                    onClose={onSubmit}
                    onCancel={onFormExit}
                />
            </form>
            <ESignatureModal
                dialogOpen={authorisersSignatureDialogOpen}
                onSignatureModalClose={() => setAuthorisersSignatureDialogOpen(false)}
                onSignatureSave={(signature) =>
                    setNewEntry({ ...newEntry, authoriserSignature: signature })
                }
                onSignatureSkip={onAuthoriserSignatureComplete}
                signerName={'Authoriser'}
            />
        </div>
    );
};

CreateFinancialRequest.propTypes = {
    roles: PropTypes.arrayOf(PropTypes.string),
    onClose: PropTypes.func
};

export default CreateFinancialRequest;
